// components/UserMenu.tsx
'use client';

import React, { useEffect, useState } from 'react';
import { useAuth, db } from '../contexts/AuthContext';
import { doc, setDoc, getDoc } from "firebase/firestore";
import { collection, getDocs, query, orderBy } from 'firebase/firestore';

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed top-4 right-4 px-4 py-2 rounded shadow-lg text-white ${type === 'success' ? 'bg-green-500' : 'bg-red-500'}`}>
      {message}
    </div>
  );
};

const UserMenu = ({ profile, setProfile }) => {
  const { user, login, logout } = useAuth();
  const [currentProfile, setCurrentProfile] = useState({});  
  const [isDirty, setIsDirty] = useState(false);
  const [showToast, setShowToast] = useState(null);
  const [showMenu, setShowMenu] = useState(false);

  // Hàm chuyển ngữ
  const t = (vi, en) => (profile.language === 'vi' ? vi : en);
  
  // Load profile from Firestore
  useEffect(() => {
    const loadProfile = async () => {
      if (user?.uid) {
        const docRef = doc(db, 'user_profiles', user.uid);
        const docSnap = await getDoc(docRef);
        docSnap.uid = user.uid;
        // print(user);
        console.log("User profile:", user);
        if (docSnap.exists()) {
          const data = docSnap.data();          
          // Bổ sung email nếu thiếu
          if (!data.email && user.email) {
            data.email = user.email;
            // Lưu lại profile có email vào Firestore
            await setDoc(docRef, { ...data, email: user.email }, { merge: true });
          }
          setCurrentProfile({ ...data, email: data.email || user.email || '' });
          setProfile({ ...data, email: data.email || user.email || '' });
        } else {
          // Auto-fill name from email
          const name = user.email?.split('@')[0].replace(/[._]/g, ' ') || '';
          const initProfile = { uid: user.uid, name : name, age: '15', grade: '9', personality: 'curiosity', interest: 'music', style: 'friendly', llm_api: 'gemini', language: 'vi', email: user.email || '' };
          setCurrentProfile(initProfile);          
          setProfile(initProfile);
          // Lưu luôn profile mới với email vào Firestore
          await setDoc(docRef, initProfile);
        }
      }
    };
    loadProfile();
  }, [user]);

  useEffect(() => {
    setIsDirty(JSON.stringify(currentProfile) !== JSON.stringify(profile));
  }, [currentProfile, profile]);
  

  const handleChange = (field, value) => {
    const updated = { ...profile, [field]: value };    
    setProfile(updated);
  };

  const saveProfile = async () => {
    try {
      if (!user?.uid) return;
      profile.uid = user.uid;
      profile.email = user.email || profile.email || '';
      await setDoc(doc(db, 'user_profiles', user.uid), profile);
      setCurrentProfile(profile);
      setShowToast({ message: 'Hồ sơ đã được lưu!', type: 'success' });
      setShowMenu(false);
    } catch (err) {
      console.error(err);
      setShowToast({ message: 'Lỗi khi lưu hồ sơ.', type: 'error' });
    }
  };

  // Đóng menu khi click ra ngoài
  React.useEffect(() => {
    if (!showMenu) return;
    const handleClickOutside = (event) => {
      // Kiểm tra nếu click không nằm trong menu
      const menu = document.getElementById('user-menu-dropdown');
      if (menu && !menu.contains(event.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);

  if (!user) {
    return (
      <button onClick={login} className="bg-blue-500 text-white px-4 py-2 rounded">
        Đăng nhập
      </button>
    );
  }


  return (
    <div className="relative">
      <img
        src={user.photoURL}
        alt="User Avatar"
        className="w-10 h-10 rounded-full cursor-pointer"
        onClick={() => setShowMenu(!showMenu)}
      />
      {showMenu && (
        <div
          id="user-menu-dropdown"
          className="absolute right-0 mt-2 w-[280px] bg-white border rounded shadow-lg p-4 z-50"
        >
          <div className="flex items-center space-x-2 mb-4">
            <img src={user.photoURL} alt="User Avatar" className="w-10 h-10 rounded-full" />
            <div>
              <p className="font-semibold">{user.displayName}</p>
              <p className="text-sm text-gray-500">{user.email}</p>
            </div>
          </div>
          <div className="space-y-2">
            <input
              placeholder={t("Tên", "Name")}
              title={t("Nhập tên của bạn", "Enter your name")}
              className="border p-1 w-full"
              value={profile.name}
              onChange={(e) => setProfile({ ...profile, name: e.target.value })}
            />
            <input
              placeholder={t("Tuổi", "Age")}
              title={t("Nhập tuổi của bạn", "Enter your age")} 
              className="border p-1 w-full"
              value={profile.age}
              onChange={(e) => setProfile({ ...profile, age: e.target.value })}
            />
            <select
              className="border p-1 w-full"
              value={profile.grade || ""}
              title={t("Chọn lớp đang học", "Select your grade")}
              onChange={e => setProfile({ ...profile, grade: e.target.value })}
            >
              <option value="9">{t("Lớp 9", "Grade 9")}</option>
              <option value="10">{t("Lớp 10", "Grade 10")}</option>
              <option value="11">{t("Lớp 11", "Grade 11")}</option>
              <option value="12">{t("Lớp 12", "Grade 12")}</option>
            </select>
            
            <input
              placeholder={t("Tính cách", "Personality")}
              title={t("Nhập tính cách của bạn", "Enter your personality traits")}
              className="border p-1 w-full"
              value={profile.personality}
              onChange={(e) => setProfile({ ...profile, personality: e.target.value })}
            />
            <input
              placeholder={t("Sở thích", "Interest")}
              title={t("Nhập sở thích của bạn", "Enter your interests")}
              className="border p-1 w-full"
              value={profile.interest}
              onChange={(e) => setProfile({ ...profile, interest: e.target.value })}
            />
            <select
              className="border p-1 w-full"
              value={profile.style}
              title={t("Chọn phong cách giao tiếp", "Select communication style")}
              onChange={(e) => setProfile({ ...profile, style: e.target.value })}
            >
              <option value="friendly">{t("Bạn bè", "Friendly")}</option>
              <option value="academic">{t("Học thuật", "Academic")}</option>
              <option value="formal">{t("Trang trọng", "Formal")}</option>              
            </select>
            <select
              value={profile.llm_api}
              title={t("Chọn AI model sử dụng", "Select AI model to use")}
              onChange={(e) => setProfile({ ...profile, llm_api: e.target.value })}
              className="border p-1 w-full"
            >
              <option value="gemini">Gemini</option>
              <option value="openai">ChatGPT</option>
              <option value="agentic">GPT+Gemini</option>
            </select>                       
            <div className="flex justify-end space-x-2">
              <button
                onClick={saveProfile}
                disabled={!isDirty}
                className="w-1/2 bg-blue-600 text-white rounded-md h-10"
              >
                {t("Lưu hồ sơ", "Save Profile")}
              </button>
              <button
                onClick={logout}
                className="w-1/2 bg-red-500 text-white rounded h-10"
              >
                {t("Đăng xuất", "Logout")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showToast && (
        <Toast
          message={showToast.message}
          type={showToast.type}
          onClose={() => setShowToast(null)}
        />
      )}
    </div>    
  );
};


export default UserMenu;